import { useEffect, useState, useCallback, useMemo, memo, useRef, startTransition } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider,
  useMediaQuery, useTheme, Checkbox, Chip, Collapse, InputAdornment,
  CircularProgress, Pagination, Autocomplete, Tooltip
} from '@mui/material';
import { Close, Add, Delete, ShoppingCart, CheckCircle, Warning, SwapHoriz, History, ExpandMore, ExpandLess, Search, List, PersonAdd, AddBox } from '@mui/icons-material';
import LoadingOverlay from '../../common/LoadingOverlay';
import { fetchData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import VendorModal from '../Master/VendorModal';
import ItemModal from '../ProductItem/ItemModal';

// PROMAX PERFORMANCE: Web Worker for ultra-fast calculations
const createCalculationWorker = () => {
  const workerCode = `
    self.onmessage = function(e) {
      const { details, freightAmount, packingAmount, freightVATRate, packingVATRate } = e.data;
      
      // Ultra-fast calculation using optimized loops
      let amount = 0, discountAmount = 0, taxableAmount = 0;
      let vatAmount = 0, totalAmount = 0;
      
      // Single loop optimization for maximum performance
      for (let i = 0; i < details.length; i++) {
        const d = details[i];
        const amt = parseFloat(d.Amount) || 0;
        const disc = parseFloat(d.DiscountAmount) || 0;
        const tax = parseFloat(d.TaxableAmount) || 0;
        const vat = parseFloat(d.VATAmount) || 0;
        const total = parseFloat(d.TotalAmount) || 0;
        
        amount += amt;
        discountAmount += disc;
        taxableAmount += tax;
        vatAmount += vat;
        totalAmount += total;
      }

      const freightVATAmount = (freightAmount * freightVATRate) / 100;
      const packingVATAmount = (packingAmount * packingVATRate) / 100;
      const grandTotal = totalAmount + freightAmount + freightVATAmount + packingAmount + packingVATAmount;

      self.postMessage({
        Amount: amount,
        DiscountAmount: discountAmount,
        TaxableAmount: taxableAmount,
        VATAmount: vatAmount,
        TotalAmount: Math.round(grandTotal),
        FreightVATRate: freightVATRate,
        FreightVATAmount: freightVATAmount,
        PackingVATRate: packingVATRate,
        PackingVATAmount: packingVATAmount,
        RoundOff: Math.round(grandTotal) - grandTotal
      });
    };
  `;

  const blob = new Blob([workerCode], { type: 'application/javascript' });
  return new Worker(URL.createObjectURL(blob));
};

// PROMAX PERFORMANCE: Immediate UI update TableRow with zero delay
const TableRow = memo(({
  detail,
  index,
  items,
  isViewMode,
  onDetailChange,
  onItemSelect,
  onRemoveRow,
  formatCurrency,
  disabledFieldStyle,
  tinyInput,
  tinyInputCenter
}) => {
  // Local state for immediate UI updates
  const [localDetail, setLocalDetail] = useState(detail);

  // Immediate UI update without waiting for parent state
  const handleImmediateChange = useCallback((field, value) => {
    // Update local state immediately for instant UI feedback
    setLocalDetail(prev => {
      const updated = { ...prev, [field]: value };

      // Calculate immediately for instant display
      if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'VATRate'].includes(field)) {
        const quantity = Math.max(0, parseFloat(updated.Quantity) || 0);
        const rate = Math.max(0, parseFloat(updated.Rate) || 0);
        const vatRate = Math.max(0, parseFloat(updated.VATRate) || 0);
        const amount = quantity * rate;

        let discountAmount, discountPct;
        if (field === 'DiscountAmount') {
          discountAmount = Math.max(0, parseFloat(value) || 0);
          discountPct = amount > 0 ? (discountAmount / amount) * 100 : 0;
        } else {
          discountPct = Math.max(0, parseFloat(updated.DiscountPercentage) || 0);
          discountAmount = (amount * discountPct) / 100;
        }

        const taxableAmount = amount - discountAmount;
        const vatAmount = (taxableAmount * vatRate) / 100;

        return {
          ...updated,
          Amount: amount,
          DiscountPercentage: Math.round(discountPct * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: taxableAmount,
          VATAmount: vatAmount,
          TotalAmount: taxableAmount + vatAmount
        };
      }

      return updated;
    });

    // Notify parent in next frame to prevent blocking
    requestAnimationFrame(() => {
      onDetailChange(index, field, value);
    });
  }, [index, onDetailChange]);

  // Sync with parent when detail changes
  useEffect(() => {
    setLocalDetail(detail);
  }, [detail]);

  // Pre-computed values for instant display
  const itemName = useMemo(() => {
    return localDetail.ItemName || items.find(i => i.Id === parseInt(localDetail.ItemId))?.Name || '-';
  }, [localDetail.ItemName, localDetail.ItemId, items]);

  const gstAmount = useMemo(() => {
    return parseFloat(localDetail.VATAmount) || 0;
  }, [localDetail.VATAmount]);

  return (
    <tr style={{ borderBottom: '1px solid #f1f5f9', background: index % 2 === 0 ? '#fff' : '#f9fafb' }}>
      <td style={{ padding: '4px', textAlign: 'center', fontWeight: 500 }}>{index + 1}</td>
      <td style={{ padding: '4px' }}>
        {isViewMode ? (
          <TextField
            size="small"
            value={itemName}
            disabled
            sx={{ ...tinyInputCenter, width: '100%', '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.8rem', textAlign: 'left' } }}
          />
        ) : (
          <FormControl fullWidth size="small">
            <Select
              value={localDetail.ItemId || ''}
              onChange={(e) => onItemSelect(index, e.target.value)}
              disabled={isViewMode}
              displayEmpty
              sx={{ fontSize: '0.8rem', '& .MuiSelect-select': { py: 0.5 } }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
            >
              <MenuItem value="" disabled sx={{ fontSize: '0.8rem' }}>Select</MenuItem>
              {items.slice(0, 100).map(item => (
                <MenuItem key={item.Id} value={item.Id} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                  {item.Name}
                </MenuItem>
              ))}
              {items.length > 100 && (
                <MenuItem disabled sx={{ fontSize: '0.7rem', fontStyle: 'italic', color: '#6b7280' }}>
                  ... and {items.length - 100} more items. Use "Get Items" for full list.
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}
      </td>
      <td style={{ padding: '4px' }}>
        <TextField
          size="small"
          value={localDetail.HSNCode || ''}
          onChange={(e) => handleImmediateChange('HSNCode', e.target.value)}
          disabled={isViewMode}
          sx={{ ...tinyInputCenter, width: '100%' }}
        />
      </td>
      <td style={{ padding: '4px' }}>
        <TextField
          size="small"
          type="number"
          value={localDetail.Quantity || ''}
          onChange={(e) => handleImmediateChange('Quantity', e.target.value)}
          disabled={isViewMode}
          sx={{ ...tinyInputCenter, width: '100%' }}
          inputProps={{ min: 0 }}
        />
      </td>
      <td style={{ padding: '4px' }}>
        <TextField
          size="small"
          type="number"
          value={localDetail.Rate || ''}
          onChange={(e) => handleImmediateChange('Rate', e.target.value)}
          disabled={isViewMode}
          sx={{ ...tinyInput, width: '100%' }}
          inputProps={{ min: 0 }}
        />
      </td>
      <td style={{ padding: '4px' }}>
        <TextField
          size="small"
          type="number"
          value={localDetail.DiscountPercentage ?? 0}
          onChange={(e) => handleImmediateChange('DiscountPercentage', e.target.value)}
          disabled={isViewMode}
          sx={{ ...tinyInputCenter, width: '100%' }}
          inputProps={{ min: 0, max: 100 }}
          onFocus={(e) => e.target.select()}
        />
      </td>
      <td style={{ padding: '4px' }}>
        <TextField
          size="small"
          type="number"
          value={localDetail.DiscountAmount ?? 0}
          onChange={(e) => handleImmediateChange('DiscountAmount', e.target.value)}
          disabled={isViewMode}
          sx={{ ...tinyInput, width: '100%' }}
          inputProps={{ min: 0 }}
          onFocus={(e) => e.target.select()}
        />
      </td>
      <td style={{ padding: '4px', textAlign: 'right', fontWeight: 500 }}>
        {formatCurrency(localDetail.TaxableAmount)}
      </td>
      <td style={{ padding: '4px', textAlign: 'center', fontWeight: 500, fontSize: '0.8rem' }}>
        {localDetail.VATRate || 0}
      </td>
      <td style={{ padding: '4px', textAlign: 'right', fontWeight: 700, color: '#1B5E20' }}>
        {formatCurrency(gstAmount)}
      </td>
      <td style={{ padding: '4px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
        {formatCurrency(localDetail.TotalAmount)}
      </td>
      {!isViewMode && (
        <td style={{ padding: '4px', textAlign: 'right' }}>
          <IconButton size="small" onClick={() => onRemoveRow(index)} sx={{ p: 0.25, color: '#ef4444' }}>
            <Delete sx={{ fontSize: 14 }} />
          </IconButton>
        </td>
      )}
    </tr>
  );
});

const PurchaseOrderModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  orderDetails,
  setOrderDetails,
  onSave,
  onVerify,
  onConvert,
  suppliers,
  setSuppliers,
  items,
  loading,
  freightPackingGST,
  onFetchPOHistory,
  onFetchItems
}) => {
  const { companyId, branchId, userId, token } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch suppliers and currencies when modal opens
  useEffect(() => {
    if (open && companyId && token) {
      const fetchSupplierData = async () => {
        try {
          const response = await fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token);
          const suppliersData = response.response_data || response || [];
          if (setSuppliers) {
            setSuppliers(suppliersData);
          }
          console.log('FetchSuppliers - Suppliers fetched in modal:', suppliersData);
        } catch (error) {
          console.error('Error fetching suppliers in modal:', error);
        }
      };
      const fetchCurrencies = async () => {
        setCurrencyLoading(true);
        try {
          const response = await fetchData('CurrencyTypeApi', `/${companyId}`, token);
          const data = response.response_data || response || [];
          setCurrencies(Array.isArray(data) ? data : []);
        } catch (error) {
          console.error('Error fetching currencies:', error);
          setCurrencies([]);
        }
        setCurrencyLoading(false);
      };
      fetchSupplierData();
      fetchCurrencies();
      // Fetch Terms & Conditions for all modes
      fetchTermsAndConditions();
    }
  }, [open, companyId, branchId, token, setSuppliers]);

  // Sync selectedTerms when modal opens with existing terms data
  useEffect(() => {
    if (open && formData.SelectedTerms && formData.SelectedTerms.length > 0) {
      setSelectedTerms(formData.SelectedTerms);
    } else if (open) {
      setSelectedTerms([]);
    }
  }, [open, formData.SelectedTerms]);

  // State declarations
  const [modalLoading, setModalLoading] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [itemsModalOpen, setItemsModalOpen] = useState(false);
  const [itemsModalPage, setItemsModalPage] = useState(1);
  const [itemsModalPerPage] = useState(20);
  const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
  const [getItemsLoading, setGetItemsLoading] = useState(false);
  const [addingItemsLoading, setAddingItemsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [getItemsSearchValue, setGetItemsSearchValue] = useState('');
  const [itemsPerPage] = useState(50);
  const [currentItemsPage, setCurrentItemsPage] = useState(1);
  const [itemSearchValue, setItemSearchValue] = useState('');
  const [poHistoryModalOpen, setPoHistoryModalOpen] = useState(false);
  const [poHistoryData, setPoHistoryData] = useState([]);
  const [poHistoryLoading, setPoHistoryLoading] = useState(false);
  const [selectedHistoryItems, setSelectedHistoryItems] = useState([]);
  const [expandedHistoryPOId, setExpandedHistoryPOId] = useState(null);
  const [calculationTimeout, setCalculationTimeout] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [currencyLoading, setCurrencyLoading] = useState(false);

  // Terms & Conditions state
  const [tcModalOpen, setTcModalOpen] = useState(false);
  const [tcData, setTcData] = useState([]);
  const [tcLoading, setTcLoading] = useState(false);
  const [selectedTerms, setSelectedTerms] = useState([]);
  const [tcSearchValue, setTcSearchValue] = useState('');

  // Quick Add Supplier (Vendor) state
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [vendorFormData, setVendorFormData] = useState({});
  const [vendorLoading, setVendorLoading] = useState(false);

  // Quick Add Item state
  const [quickItemModalOpen, setQuickItemModalOpen] = useState(false);
  const [quickItemCategories, setQuickItemCategories] = useState([]);
  const [quickItemBrands, setQuickItemBrands] = useState([]);
  const [quickItemUoms, setQuickItemUoms] = useState([]);

  const handleOpenVendorModal = () => {
    setVendorFormData({});
    setVendorModalOpen(true);
  };

  const handleCloseVendorModal = () => {
    setVendorModalOpen(false);
    setVendorFormData({});
  };

  const handleSaveVendor = async () => {
    setVendorLoading(true);
    try {
      const dataToSend = {
        Master: {
          Id: 0,
          CompanyId: companyId,
          BalanceSheetHeadId: vendorFormData.BalanceSheetHeadId || 0,
          MasterTypeId: 1,
          Name: vendorFormData.Name || '',
          SearchableName: vendorFormData.SearchableName || vendorFormData.Name || '',
          OpeningBalance: parseFloat(vendorFormData.OpeningBalance) || 0,
          OpeningDate: vendorFormData.OpeningDate ? vendorFormData.OpeningDate.split('T')[0] : new Date().toISOString().split('T')[0],
          IsPayableOrReceivable: vendorFormData.IsPayableOrReceivable ?? false,
          IsActive: true,
          CreatedBy: userId
        },
        LedgerGroup: {
          Id: 0,
          CompanyId: companyId,
          MasterId: 0,
          Name: vendorFormData.LedgerGroupName || '',
          CreditDays: parseInt(vendorFormData.GroupCreditDays) || 0,
          CreditLimit: parseFloat(vendorFormData.GroupCreditLimit) || 0,
          IsActive: true,
          CreatedBy: userId
        },
        Ledger: {
          Id: 0,
          CompanyId: companyId,
          MasterId: 0,
          LedgerGroupId: 0,
          IsType: 2,
          InvoiceTypeId: vendorFormData.InvoiceTypeId || 0,
          SalesExecutiveId: vendorFormData.SalesExecutiveId || 0,
          StateCodeId: vendorFormData.StateCodeId || null,
          AddressLine1: vendorFormData.AddressLine1 || '',
          AddressLine2: vendorFormData.AddressLine2 || '',
          Location: vendorFormData.Location || '',
          PinCode: vendorFormData.PinCode || '',
          CountryId: vendorFormData.CountryId || null,
          ISOCode: vendorFormData.ISOCode || '',
          DeliveryAddressLine2: vendorFormData.DeliveryAddressLine2 || '',
          DeliveryLocation: vendorFormData.DeliveryLocation || '',
          DeliveryPinCode: vendorFormData.DeliveryPinCode || '',
          ContactNumber1: vendorFormData.ContactNumber1 || '',
          ContactNumber2: vendorFormData.ContactNumber2 || '',
          ContactNumber3: vendorFormData.ContactNumber3 || '',
          Email: vendorFormData.Email || '',
          VATNumber: vendorFormData.VATNumber || '',
          PanNumber: vendorFormData.PanNumber || '',
          MSMENumber: vendorFormData.MSMENumber || '',
          CreditDays: parseInt(vendorFormData.CreditDays) || 0,
          CreditLimit: parseFloat(vendorFormData.CreditLimit) || 0,
          IECCode: vendorFormData.IECCode || '',
          TradeLicenseNo: vendorFormData.TradeLicenseNo || '',
          CurrencyTypeId: vendorFormData.CurrencyTypeId || null,
          CurrencyName: vendorFormData.CurrencyName || '',
          OpeningBalance: parseFloat(vendorFormData.OpeningBalance) || 0,
          OpeningDate: vendorFormData.OpeningDate ? vendorFormData.OpeningDate.split('T')[0] : new Date().toISOString().split('T')[0],
          IsPayableOrReceivable: false,
          IsActive: true,
          CreatedBy: userId
        }
      };

      await postData('ClientAndVendorApi', '', dataToSend, token);

      // Refresh supplier list
      const response = await fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token);
      const updatedSuppliers = response.response_data || response || [];
      if (setSuppliers) setSuppliers(updatedSuppliers);

      // Auto-select the newly created supplier by name
      const newSupplier = updatedSuppliers.find(s => s.Name === vendorFormData.Name);
      if (newSupplier) {
        handleChange('SupplierId', newSupplier.MasterId || newSupplier.Id);
      }

      handleCloseVendorModal();
    } catch (error) {
      console.error('Error saving vendor:', error);
    }
    setVendorLoading(false);
  };

  // Quick Add Item handlers
  const handleOpenQuickItemModal = async () => {
    // Fetch categories, brands, uoms for the item modal
    try {
      const [catRes, brandRes, uomRes] = await Promise.allSettled([
        fetchData('CategoryApi', `/${companyId}`, token),
        fetchData('BrandApi', `/${companyId}`, token),
        fetchData('UOMApi', `/${companyId}`, token)
      ]);
      setQuickItemCategories(catRes.status === 'fulfilled' ? (catRes.value.response_data || catRes.value || []) : []);
      setQuickItemBrands(brandRes.status === 'fulfilled' ? (brandRes.value.response_data || brandRes.value || []) : []);
      setQuickItemUoms(uomRes.status === 'fulfilled' ? (uomRes.value.response_data || uomRes.value || []) : []);
    } catch (e) {
      console.error('Error fetching item master data:', e);
    }
    setQuickItemModalOpen(true);
  };

  const handleCloseQuickItemModal = () => {
    setQuickItemModalOpen(false);
  };

  const handleQuickItemSaved = async () => {
    // Refresh items list after new item is created
    if (onFetchItems) {
      await onFetchItems('');
    }
    setQuickItemModalOpen(false);
  };

  console.log('PurchaseOrderModal - Suppliers received:', suppliers);

  const isViewMode = modalAction === 'view';
  const isInternationalPO = formData.CurrencyName && formData.CurrencyName.toLowerCase().trim() !== 'aed' && formData.CurrencyName.toLowerCase().trim() !== 'dirham' && formData.CurrencyName.toLowerCase().trim() !== 'uae dirham';
  const title = modalAction === 'create' 
    ? (isInternationalPO ? 'Create International Purchase Order' : 'Create Purchase Order') 
    : modalAction === 'edit' 
      ? (isInternationalPO ? 'Edit International Purchase Order' : 'Edit Purchase Order') 
      : (isInternationalPO ? 'View International Purchase Order' : 'View Purchase Order');

  // Fetch Terms & Conditions
  const fetchTermsAndConditions = async () => {
    if (!companyId || !token) return;
    setTcLoading(true);
    try {
      const response = await fetchData('PurchaseOrderApi', `/PurchaseTerms/${companyId}/${branchId || 1}`, token);
      const termsData = response.response_data || response || [];
      setTcData(Array.isArray(termsData) ? termsData : []);
      // Set selection from formData (already set in handleOpenTcModal, but ensure after data loads)
      setSelectedTerms(formData.SelectedTerms || []);
    } catch (error) {
      console.error('Error fetching terms and conditions:', error);
      setTcData([]);
    } finally {
      setTcLoading(false);
    }
  };

  const handleOpenTcModal = () => {
    // Always reset to formData selection when opening
    setSelectedTerms(formData.SelectedTerms || []);
    setTcModalOpen(true);
    fetchTermsAndConditions();
  };

  const handleCloseTcModal = () => {
    // Discard changes - reset back to formData selection
    setSelectedTerms(formData.SelectedTerms || []);
    setTcModalOpen(false);
  };

  const handleToggleTerm = (termId) => {
    setSelectedTerms(prev => {
      const newSelection = prev.includes(termId) 
        ? prev.filter(id => id !== termId)
        : [...prev, termId];
      
      // Directly update formData on each toggle
      const selectedTermsData = tcData.filter(term => newSelection.includes(term.Id));
      setFormData(prevForm => ({ 
        ...prevForm, 
        SelectedTerms: newSelection,
        SelectedTermsData: selectedTermsData,
        TermsAndConditions: selectedTermsData.map(t => t.Name).join('\n')
      }));
      
      return newSelection;
    });
  };

  const handleSelectAllTerms = () => {
    let newSelection;
    if (selectedTerms.length === tcData.length) {
      newSelection = [];
    } else {
      newSelection = tcData.map(term => term.Id);
    }
    setSelectedTerms(newSelection);
    
    // Directly update formData
    const selectedTermsData = tcData.filter(term => newSelection.includes(term.Id));
    setFormData(prev => ({ 
      ...prev, 
      SelectedTerms: newSelection,
      SelectedTermsData: selectedTermsData,
      TermsAndConditions: selectedTermsData.map(t => t.Name).join('\n')
    }));
  };

  const handleApplyTerms = () => {
    // Save selected terms to formData
    const selectedTermsData = tcData.filter(term => selectedTerms.includes(term.Id));
    setFormData(prev => ({ 
      ...prev, 
      SelectedTerms: selectedTerms,
      SelectedTermsData: selectedTermsData,
      TermsAndConditions: selectedTermsData.map(t => t.Name).join('\n')
    }));
    setTcModalOpen(false);
  };

  // PROMAX PERFORMANCE: Web Worker for calculations
  const calculationWorkerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const calculationTimeoutRef = useRef(null);
  const orderDetailsRef = useRef(orderDetails);
  const formDataRef = useRef(formData);

  // Initialize Web Worker
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;

    if (!calculationWorkerRef.current) {
      calculationWorkerRef.current = createCalculationWorker();

      calculationWorkerRef.current.onmessage = (e) => {
        if (isMountedRef.current) {
          const calculatedTotals = e.data;
          startTransition(() => {
            setFormData(prev => ({ ...prev, ...calculatedTotals }));
          });
        }
      };
    }

    return () => {
      isMountedRef.current = false;
      if (calculationWorkerRef.current) {
        calculationWorkerRef.current.terminate();
        calculationWorkerRef.current = null;
      }
    };
  }, []);

  // Update refs when props change
  useEffect(() => {
    orderDetailsRef.current = orderDetails;
    formDataRef.current = formData;
  }, [orderDetails, formData]);

  // PROMAX PERFORMANCE: Web Worker-based calculations with zero main thread blocking
  const performCalculations = useCallback((details) => {
    if (!calculationWorkerRef.current || details.length === 0) {
      return;
    }

    const currentFormData = formDataRef.current;
    const defaultFreightVATRate = parseFloat(freightPackingGST?.freight?.VATRate || freightPackingGST?.freight?.GSTRate || freightPackingGST?.freight?.GstRate) || 0;
    const defaultPackingVATRate = parseFloat(freightPackingGST?.packing?.VATRate || freightPackingGST?.packing?.GSTRate || freightPackingGST?.packing?.GstRate) || 0;

    // Get max item VAT rate from order details (same logic as SaleModal)
    const maxItemVATRate = details.length > 0 ? Math.max(...details.map(d => parseFloat(d.VATRate) || 0)) : 0;
    const effectiveFreightVATRate = Math.max(defaultFreightVATRate, maxItemVATRate);
    const effectivePackingVATRate = Math.max(defaultPackingVATRate, maxItemVATRate);

    const freightAmount = parseFloat(currentFormData.FreightAmount) || 0;
    const packingAmount = parseFloat(currentFormData.PackingAmount) || 0;

    // Send to Web Worker for non-blocking calculation
    calculationWorkerRef.current.postMessage({
      details,
      freightAmount,
      packingAmount,
      freightVATRate: effectiveFreightVATRate,
      packingVATRate: effectivePackingVATRate
    });
  }, [freightPackingGST]);

  // Fetch PO history when supplier is selected
  const fetchPOHistoryForSupplier = useCallback(async (supplierId) => {
    if (!onFetchPOHistory) return;

    setPoHistoryLoading(true);
    setExpandedHistoryPOId(null);
    try {
      const historyData = await onFetchPOHistory(supplierId);
      if (historyData && historyData.length > 0) {
        setPoHistoryData(historyData);

        // Pre-select items that are already in orderDetails
        const currentOrderDetails = orderDetailsRef.current;
        const alreadyAddedItems = [];
        historyData.forEach(po => {
          (po.PurchaseOrderDetails || []).forEach(historyItem => {
            // Check if this item is already in orderDetails (by ItemId)
            const isAlreadyAdded = currentOrderDetails.some(
              detail => {
                const detailItemId = detail.ItemId || detail.Id;
                return parseInt(detailItemId) === parseInt(historyItem.ItemId);
              }
            );
            if (isAlreadyAdded) {
              alreadyAddedItems.push({ ...historyItem, poId: po.Id });
            }
          });
        });
        setSelectedHistoryItems(alreadyAddedItems);

        setPoHistoryModalOpen(true);
      } else {
        setPoHistoryData([]);
        setSelectedHistoryItems([]);
      }
    } catch (error) {
      console.error('Error fetching PO history:', error);
      setPoHistoryData([]);
      setSelectedHistoryItems([]);
    }
    setPoHistoryLoading(false);
  }, [onFetchPOHistory]);

  // Handle form field changes
  const handleChange = useCallback((field, value) => {
    if (field === 'SupplierId') {
      const selectedSupplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(value));
      
      // Fetch full supplier details from API to get CurrencyName and CurrencyTypeId
      const fetchSupplierDetails = async () => {
        try {
          const response = await fetchData('ClientAndVendorApi', `/ClientAndVendorDetails/${value}`, token);
          const vendorData = response.response_data?.Ledger || response.response_data || response || {};
          
          let currencyTypeId = vendorData.CurrencyTypeId || null;
          let currencyName = vendorData.CurrencyName || '';
          
          console.log('Selected Supplier Currency Info from API:', { supplierName: vendorData.Name || selectedSupplier?.Name, CurrencyName: currencyName, CurrencyTypeId: currencyTypeId });
          
          // Ensure currencies list is loaded before matching
          let currenciesList = currencies;
          if (!Array.isArray(currenciesList) || currenciesList.length === 0) {
            try {
              const currRes = await fetchData('CurrencyTypeApi', `/${companyId}`, token);
              currenciesList = currRes.response_data || currRes || [];
              setCurrencies(Array.isArray(currenciesList) ? currenciesList : []);
            } catch (e) {
              currenciesList = [];
            }
          }

          // Match currency from the currencies list
          let matchedCurrency = null;
          if (Array.isArray(currenciesList) && currenciesList.length > 0) {
            if (currencyTypeId) {
              matchedCurrency = currenciesList.find(c => String(c.Id) === String(currencyTypeId));
            }
            if (!matchedCurrency && currencyName) {
              const nameLower = currencyName.toLowerCase().trim();
              matchedCurrency = currenciesList.find(c => c.Currency && c.Currency.toLowerCase().trim() === nameLower);
              if (!matchedCurrency) {
                matchedCurrency = currenciesList.find(c => c.Currency && (
                  c.Currency.toLowerCase().includes(nameLower) || nameLower.includes(c.Currency.toLowerCase())
                ));
              }
            }
          }

          const finalCurrencyTypeId = matchedCurrency?.Id || currencyTypeId;
          const finalCurrencyName = matchedCurrency?.Currency || currencyName;
          
          // Update PO prefix based on currency: IPO for International, LPO for Local (AED)
          const isLocalCurrency = finalCurrencyName && ['aed', 'dirham', 'uae dirham'].includes(finalCurrencyName.toLowerCase().trim());
          const newPrefix = isLocalCurrency ? 'LPO' : 'IPO';
          
          setFormData(prev => {
            const poNumber = prev.PONumber || '';
            const finYear = prev.FinYear || '';
            let newPONo = '';
            if (finYear) {
              newPONo = `${newPrefix}/${poNumber}/${finYear}`;
            } else {
              newPONo = `${newPrefix}/${poNumber}`;
            }
            return { ...prev, [field]: value, CurrencyName: finalCurrencyName, CurrencyTypeId: finalCurrencyTypeId, PurchaseOrderNo: newPONo };
          });
        } catch (error) {
          console.error('Error fetching supplier details:', error);
          // Fallback to supplier list data if API fails
          const currencyName = selectedSupplier?.CurrencyName || '';
          
          let matchedCurrency = null;
          if (Array.isArray(currencies) && currencies.length > 0) {
            if (selectedSupplier?.CurrencyTypeId) {
              matchedCurrency = currencies.find(c => String(c.Id) === String(selectedSupplier.CurrencyTypeId));
            }
            if (!matchedCurrency && currencyName) {
              const nameLower = currencyName.toLowerCase().trim();
              matchedCurrency = currencies.find(c => c.Currency && c.Currency.toLowerCase().trim() === nameLower);
              if (!matchedCurrency) {
                matchedCurrency = currencies.find(c => c.Currency && (
                  c.Currency.toLowerCase().includes(nameLower) || nameLower.includes(c.Currency.toLowerCase())
                ));
              }
            }
          }
          
          const currencyTypeId = matchedCurrency?.Id || selectedSupplier?.CurrencyTypeId || null;
          const finalCurrencyName = matchedCurrency?.Currency || currencyName;
          
          const isLocalCurrency = finalCurrencyName && ['aed', 'dirham', 'uae dirham'].includes(finalCurrencyName.toLowerCase().trim());
          const newPrefix = isLocalCurrency ? 'LPO' : 'IPO';
          
          setFormData(prev => {
            const poNumber = prev.PONumber || '';
            const finYear = prev.FinYear || '';
            let newPONo = '';
            if (finYear) {
              newPONo = `${newPrefix}/${poNumber}/${finYear}`;
            } else {
              newPONo = `${newPrefix}/${poNumber}`;
            }
            return { ...prev, [field]: value, CurrencyName: finalCurrencyName, CurrencyTypeId: currencyTypeId, PurchaseOrderNo: newPONo };
          });
        }
      };
      
      fetchSupplierDetails();

      // Fetch PO history for the selected supplier (only in create mode)
      if (modalAction === 'create' && value && onFetchPOHistory) {
        fetchPOHistoryForSupplier(value);
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  }, [suppliers, currencies, setCurrencies, companyId, modalAction, onFetchPOHistory, fetchPOHistoryForSupplier, token]);

  // Memoized set of already added item IDs for better performance
  const addedItemIds = useMemo(() => {
    if (!orderDetails || orderDetails.length === 0) return new Set();

    return new Set(orderDetails.map(detail => {
      const itemId = detail.ItemId || detail.Id;
      return parseInt(itemId);
    }).filter(id => !isNaN(id)));
  }, [orderDetails.length, orderDetails.map(d => d.ItemId || d.Id).join(',')]);

  // Check if item is already added to order (optimized with Set lookup)
  const isItemAlreadyInOrder = useCallback((itemId) => {
    return addedItemIds.has(parseInt(itemId));
  }, [addedItemIds]);

  // Toggle item selection in history modal
  const toggleHistoryItemSelection = (item, poId) => {
    const itemKey = `${poId}-${item.ItemId}`;
    setSelectedHistoryItems(prev => {
      const exists = prev.find(i => `${i.poId}-${i.ItemId}` === itemKey);
      if (exists) {
        return prev.filter(i => `${i.poId}-${i.ItemId}` !== itemKey);
      } else {
        return [...prev, { ...item, poId }];
      }
    });
  };

  // Check if item is selected
  const isHistoryItemSelected = (item, poId) => {
    const itemKey = `${poId}-${item.ItemId}`;
    return selectedHistoryItems.some(i => `${i.poId}-${i.ItemId}` === itemKey);
  };

  // Get all items from history for select all functionality
  const getAllHistoryItems = () => {
    const allItems = [];
    poHistoryData.forEach(po => {
      (po.PurchaseOrderDetails || []).forEach(item => {
        allItems.push({ ...item, poId: po.Id });
      });
    });
    return allItems;
  };

  // Select/Deselect all items
  const toggleSelectAllHistoryItems = () => {
    const allItems = getAllHistoryItems();
    if (selectedHistoryItems.length === allItems.length) {
      setSelectedHistoryItems([]);
    } else {
      setSelectedHistoryItems(allItems);
    }
  };

  // Get all items from a specific PO for bill-wise select
  const getPOItems = (poId) => {
    const po = poHistoryData.find(p => p.Id === poId);
    if (!po) return [];
    return (po.PurchaseOrderDetails || []).map(item => ({ ...item, poId }));
  };

  // Check if all items from a specific PO are selected
  const isPOFullySelected = (poId) => {
    const poItems = getPOItems(poId);
    if (poItems.length === 0) return false;
    return poItems.every(item => isHistoryItemSelected(item, poId));
  };

  // Check if some (but not all) items from a specific PO are selected
  const isPOPartiallySelected = (poId) => {
    const poItems = getPOItems(poId);
    if (poItems.length === 0) return false;
    const selectedCount = poItems.filter(item => isHistoryItemSelected(item, poId)).length;
    return selectedCount > 0 && selectedCount < poItems.length;
  };

  // Toggle select all items from a specific PO (bill-wise select)
  const togglePOSelection = (poId) => {
    const poItems = getPOItems(poId);
    if (isPOFullySelected(poId)) {
      // Deselect all items from this PO
      setSelectedHistoryItems(prev =>
        prev.filter(item => item.poId !== poId)
      );
    } else {
      // Select all items from this PO
      setSelectedHistoryItems(prev => {
        // Remove any existing items from this PO first, then add all
        const otherItems = prev.filter(item => item.poId !== poId);
        return [...otherItems, ...poItems];
      });
    }
  };

  // Add selected items from history to order details
  const addSelectedHistoryItems = () => {
    if (selectedHistoryItems.length === 0) return;

    // Filter out items that are already in orderDetails (avoid duplicates)
    const itemsToAdd = selectedHistoryItems.filter(
      historyItem => !orderDetails.some(detail => {
        const detailItemId = detail.ItemId || detail.Id;
        return parseInt(detailItemId) === parseInt(historyItem.ItemId);
      })
    );

    if (itemsToAdd.length === 0) {
      // All selected items are already in the order, just close the modal
      setPoHistoryModalOpen(false);
      setSelectedHistoryItems([]);
      setExpandedHistoryPOId(null);
      return;
    }

    const newItems = itemsToAdd.map(historyItem => {
      const quantity = parseFloat(historyItem.Quantity) || 0;
      const rate = parseFloat(historyItem.Rate) || 0;
      const vatRate = parseFloat(historyItem.VATRate || historyItem.GSTRate) || 0;
      const discountPct = parseFloat(historyItem.DiscountPercentage) || 0;
      const amount = quantity * rate;
      const discountAmount = (amount * discountPct) / 100;
      const taxableAmount = amount - discountAmount;
      const vatAmount = (taxableAmount * vatRate) / 100;

      return {
        Id: 0,
        ItemId: historyItem.ItemId,
        HSNCode: historyItem.HSNCode || '',
        VATRate: vatRate,
        Quantity: quantity,
        Rate: rate,
        Amount: amount,
        DiscountPercentage: discountPct,
        DiscountAmount: discountAmount,
        TaxableAmount: taxableAmount,
        VATAmount: vatAmount,
        TotalAmount: taxableAmount + vatAmount,
        IsActive: true
      };
    });

    const updatedDetails = [...orderDetails, ...newItems];
    setOrderDetails(updatedDetails);
    performCalculations(updatedDetails);

    // Close history modal and reset
    setPoHistoryModalOpen(false);
    setSelectedHistoryItems([]);
    setExpandedHistoryPOId(null);
  };

  // Close history modal without adding items
  const closeHistoryModal = () => {
    setPoHistoryModalOpen(false);
    setSelectedHistoryItems([]);
    setExpandedHistoryPOId(null);
  };

  // Handle item search with debouncing
  const handleItemSearch = (searchValue) => {
    setItemSearchValue(searchValue);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search (300ms delay)
    searchTimeoutRef.current = setTimeout(async () => {
      if (onFetchItems) {
        await onFetchItems(searchValue);
      }
    }, 300);
  };

  // Handle Get Items modal
  const handleOpenGetItemsModal = async () => {
    setGetItemsModalOpen(true);
    setGetItemsLoading(true);
    setSelectedItems([]);
    setGetItemsSearchValue('');
    setCurrentItemsPage(1);

    const loadingTimeout = setTimeout(() => {
      console.warn('Get Items loading timeout - stopping loading');
      setGetItemsLoading(false);
    }, 3000);

    try {
      if (onFetchItems) {
        await onFetchItems('');
      } else {
        console.warn('onFetchItems function not provided');
      }
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      clearTimeout(loadingTimeout);
      setGetItemsLoading(false);
    }
  };

  const handleCloseGetItemsModal = () => {
    setGetItemsModalOpen(false);
    setSelectedItems([]);
    setGetItemsSearchValue('');
  };

  // Handle item selection in Get Items modal
  const handleItemSelection = (item, isSelected) => {
    if (isSelected) {
      const newSelectedItem = {
        ...item,
        selectedQuantity: 1,
        selectedRate: item.PurchaseRate || item.SaleRate || 0,
        selectedDiscountPercentage: 0,
        selectedDiscountAmount: 0
      };
      setSelectedItems(prev => [...prev, newSelectedItem]);
    } else {
      setSelectedItems(prev => prev.filter(selectedItem => selectedItem.Id !== item.Id));
    }
  };

  // Update selected item details
  const handleSelectedItemChange = (itemId, field, value) => {
    setSelectedItems(prev => prev.map(item =>
      item.Id === itemId ? { ...item, [field]: value } : item
    ));
  };

  // Add selected items to order details
  const handleAddSelectedItems = async () => {
    if (selectedItems.length === 0) return;

    setAddingItemsLoading(true);

    try {
      const itemsToAdd = selectedItems.filter(
        selectedItem => !addedItemIds.has(parseInt(selectedItem.Id))
      );

      if (itemsToAdd.length === 0) {
        handleCloseGetItemsModal();
        return;
      }

      const chunkSize = 50;
      const chunks = [];
      for (let i = 0; i < itemsToAdd.length; i += chunkSize) {
        chunks.push(itemsToAdd.slice(i, i + chunkSize));
      }

      let allNewItems = [];

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunk = chunks[chunkIndex];

        const chunkItems = chunk.map(selectedItem => {
          const quantity = parseFloat(selectedItem.selectedQuantity) || 0;
          const rate = parseFloat(selectedItem.selectedRate) || 0;
          const vatRate = parseFloat(selectedItem.VATRate || selectedItem.GSTRate) || 0;
          const discountPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
          const discountAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;
          const amount = quantity * rate;

          const finalDiscountAmount = discountAmt > 0 ? discountAmt : (amount * discountPct) / 100;
          const taxableAmount = amount - finalDiscountAmount;
          const vatAmount = (taxableAmount * vatRate) / 100;

          return {
            Id: 0,
            ItemId: selectedItem.Id,
            HSNCode: selectedItem.HSNCode || '',
            VATRate: vatRate,
            Quantity: quantity,
            Rate: rate,
            Amount: amount,
            DiscountPercentage: discountAmt > 0 ? (finalDiscountAmount / amount) * 100 : discountPct,
            DiscountAmount: finalDiscountAmount,
            TaxableAmount: taxableAmount,
            VATAmount: vatAmount,
            TotalAmount: taxableAmount + vatAmount,
            IsActive: true
          };
        });

        allNewItems = [...allNewItems, ...chunkItems];

        if (itemsToAdd.length > 100 && chunkIndex < chunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }

      const updatedDetails = [...orderDetails, ...allNewItems];
      setOrderDetails(updatedDetails);

      handleCloseGetItemsModal();

      setTimeout(() => {
        performCalculations(updatedDetails);
      }, 0);

    } catch (error) {
      console.error('Error adding selected items:', error);
    } finally {
      setAddingItemsLoading(false);
    }
  };

  // Handle search in Get Items modal
  const handleGetItemsSearch = (searchValue) => {
    setGetItemsSearchValue(searchValue);
    setCurrentItemsPage(1);
  };

  // Memoized filtered items for better performance
  const filteredItems = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    if (!getItemsSearchValue.trim()) {
      return items;
    }

    const searchTerm = getItemsSearchValue.toLowerCase().trim();
    return items.filter(item =>
      item.Name?.toLowerCase().includes(searchTerm) ||
      item.HSNCode?.toLowerCase().includes(searchTerm)
    );
  }, [items, getItemsSearchValue]);

  // Get paginated items for performance
  const getPaginatedItems = useMemo(() => {
    const startIndex = (currentItemsPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, currentItemsPage, itemsPerPage]);

  // Calculate total pages
  const getTotalPages = useMemo(() => {
    return Math.ceil(filteredItems.length / itemsPerPage);
  }, [filteredItems.length, itemsPerPage]);

  // Pagination for "See More" modal
  const getPaginatedOrderDetails = useMemo(() => {
    const startIndex = (itemsModalPage - 1) * itemsModalPerPage;
    const endIndex = startIndex + itemsModalPerPage;
    return orderDetails.slice(startIndex, endIndex);
  }, [orderDetails, itemsModalPage, itemsModalPerPage]);

  const getOrderDetailsTotalPages = useMemo(() => {
    return Math.ceil(orderDetails.length / itemsModalPerPage);
  }, [orderDetails.length, itemsModalPerPage]);

  // PROMAX PERFORMANCE: Zero-delay detail change with immediate UI updates
  const handleDetailChange = useCallback((index, field, value) => {
    // Early validation for numeric fields
    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount'].includes(field)) {
      if (value !== '' && value !== null && value !== undefined) {
        if (!/^\d*\.?\d*$/.test(value)) return;
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0) return;
      }
    }

    // Update state immediately for instant UI feedback
    const updatedDetails = orderDetailsRef.current.slice();
    const currentDetail = updatedDetails[index];
    updatedDetails[index] = { ...currentDetail, [field]: value };

    // Immediate calculation for affected row only
    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'VATRate'].includes(field)) {
      const detail = updatedDetails[index];
      const quantity = Math.max(0, parseFloat(detail.Quantity) || 0);
      const rate = Math.max(0, parseFloat(detail.Rate) || 0);
      const vatRate = Math.max(0, parseFloat(detail.VATRate) || 0);
      const amount = quantity * rate;

      let discountAmount, discountPct;
      if (field === 'DiscountAmount') {
        discountAmount = Math.max(0, parseFloat(value) || 0);
        discountPct = amount > 0 ? (discountAmount / amount) * 100 : 0;
      } else {
        discountPct = Math.max(0, parseFloat(detail.DiscountPercentage) || 0);
        discountAmount = (amount * discountPct) / 100;
      }

      const taxableAmount = amount - discountAmount;
      const vatAmount = (taxableAmount * vatRate) / 100;

      Object.assign(updatedDetails[index], {
        Amount: amount,
        DiscountPercentage: Math.round(discountPct * 100) / 100,
        DiscountAmount: Math.round(discountAmount * 100) / 100,
        TaxableAmount: taxableAmount,
        VATAmount: vatAmount,
        TotalAmount: taxableAmount + vatAmount
      });
    }

    // Update state immediately
    setOrderDetails(updatedDetails);

    // Debounce calculation to prevent excessive updates
    if (calculationTimeoutRef.current) clearTimeout(calculationTimeoutRef.current);
    calculationTimeoutRef.current = setTimeout(() => {
      performCalculations(updatedDetails);
    }, 200);
  }, [performCalculations]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
    };
  }, []);

  // Reset search when modal opens
  useEffect(() => {
    if (open && !isViewMode) {
      setItemSearchValue('');
      if (onFetchItems) {
        onFetchItems('');
      }
    }
  }, [open, isViewMode, onFetchItems]);

  // Handle modal loading state when opening
  useEffect(() => {
    if (open) {
      setModalLoading(false);
    } else {
      setModalLoading(false);
    }
  }, [open]);

  // Recalculate when freight/packing amounts or VAT rates change
  const freightPackingRef = useRef({
    FreightAmount: formData.FreightAmount,
    FreightVATRate: formData.FreightVATRate,
    PackingAmount: formData.PackingAmount,
    PackingVATRate: formData.PackingVATRate
  });

  useEffect(() => {
    const current = {
      FreightAmount: formData.FreightAmount,
      FreightVATRate: formData.FreightVATRate,
      PackingAmount: formData.PackingAmount,
      PackingVATRate: formData.PackingVATRate
    };

    // Only update if values actually changed
    const hasChanged = Object.keys(current).some(key =>
      freightPackingRef.current[key] !== current[key]
    );

    if (hasChanged) {
      freightPackingRef.current = current;

      if (!isViewMode && orderDetails.length > 0) {
        if (calculationTimeoutRef.current) clearTimeout(calculationTimeoutRef.current);
        calculationTimeoutRef.current = setTimeout(() => {
          performCalculations(orderDetails);
        }, 300);
      }
    }
  }, [formData.FreightAmount, formData.FreightVATRate, formData.PackingAmount, formData.PackingVATRate, isViewMode, orderDetails.length]);

  // Create items map for O(1) lookups
  const itemsMap = useMemo(() => {
    const map = new Map();
    items.forEach(item => map.set(item.Id, item));
    return map;
  }, [items]);

  const handleItemSelect = useCallback((index, itemId) => {
    const selectedItem = itemsMap.get(parseInt(itemId));
    if (selectedItem) {
      const updatedDetails = orderDetailsRef.current.slice();
      const currentDetail = updatedDetails[index];
      const quantity = parseFloat(currentDetail.Quantity) || 0;
      const rate = selectedItem.PurchaseRate || selectedItem.SaleRate || 0;
      const vatRate = selectedItem.VATRate || selectedItem.GSTRate || 0;
      const discountPct = parseFloat(currentDetail.DiscountPercentage) || 0;
      const amount = quantity * rate;
      const discountAmount = (amount * discountPct) / 100;
      const taxableAmount = amount - discountAmount;
      const vatAmount = (taxableAmount * vatRate) / 100;

      Object.assign(updatedDetails[index], {
        ItemId: itemId,
        HSNCode: selectedItem.HSNCode || '',
        VATRate: vatRate,
        Rate: rate,
        Amount: amount,
        DiscountAmount: discountAmount,
        TaxableAmount: taxableAmount,
        VATAmount: vatAmount,
        TotalAmount: taxableAmount + vatAmount
      });

      setOrderDetails(updatedDetails);

      // Debounce calculation to prevent excessive updates
      if (calculationTimeoutRef.current) clearTimeout(calculationTimeoutRef.current);
      calculationTimeoutRef.current = setTimeout(() => {
        performCalculations(updatedDetails);
      }, 200);
    }
  }, [itemsMap, performCalculations]);

  const addDetailRow = useCallback(() => {
    setOrderDetails(prev => [{
      Id: 0, ItemId: '', HSNCode: '', VATRate: 0, Quantity: 0, Rate: 0, Amount: 0,
      DiscountPercentage: 0, DiscountAmount: 0, TaxableAmount: 0, VATAmount: 0, TotalAmount: 0, IsActive: true
    }, ...prev]);
  }, []);

  const removeDetailRow = useCallback((index) => {
    const updatedDetails = orderDetailsRef.current.filter((_, i) => i !== index);
    setOrderDetails(updatedDetails);

    // Debounce calculation to prevent excessive updates
    if (calculationTimeoutRef.current) clearTimeout(calculationTimeoutRef.current);
    calculationTimeoutRef.current = setTimeout(() => {
      performCalculations(updatedDetails);
    }, 200);
  }, [performCalculations]);

  const supplierDisplayName = useMemo(() => {
    if (formData.MasterName) return formData.MasterName;
    if (!formData.SupplierId) return '';
    const supplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(formData.SupplierId));
    return supplier?.Name || '';
  }, [formData.MasterName, formData.SupplierId, suppliers]);

  const supplierCurrencyName = useMemo(() => {
    // Use formData.CurrencyName which can be overridden by user via dropdown
    return formData.CurrencyName || null;
  }, [formData.CurrencyName]);

  // Auto-sync CurrencyTypeId when currencies list loads and CurrencyName is already set
  useEffect(() => {
    if (currencies.length > 0 && formData.CurrencyName) {
      // Check if current CurrencyTypeId actually matches something in the currencies list
      const currentMatch = formData.CurrencyTypeId 
        ? currencies.find(c => String(c.Id) === String(formData.CurrencyTypeId))
        : null;
      if (!currentMatch) {
        // CurrencyTypeId doesn't match anything - try to find by name
        const nameLower = formData.CurrencyName.toLowerCase().trim();
        const matched = currencies.find(c => c.Currency && c.Currency.toLowerCase().trim() === nameLower);
        if (!matched) {
          // Try partial match
          const partialMatch = currencies.find(c => c.Currency && (
            c.Currency.toLowerCase().includes(nameLower) || nameLower.includes(c.Currency.toLowerCase())
          ));
          if (partialMatch) {
            setFormData(prev => ({ ...prev, CurrencyTypeId: partialMatch.Id, CurrencyName: partialMatch.Currency }));
          }
        } else {
          setFormData(prev => ({ ...prev, CurrencyTypeId: matched.Id, CurrencyName: matched.Currency }));
        }
      }
    }
  }, [currencies, formData.CurrencyName, formData.CurrencyTypeId]);

  // Currency symbol mapping based on CurrencyName
  const currencySymbol = useMemo(() => {
    if (!supplierCurrencyName) return '₹';
    const name = supplierCurrencyName.toLowerCase().trim();
    const symbolMap = {
      'inr': '₹', 'rupee': '₹', 'indian rupee': '₹',
      'usd': '$', 'dollar': '$', 'us dollar': '$',
      'eur': '€', 'euro': '€',
      'gbp': '£', 'pound': '£', 'british pound': '£',
      'aed': 'د.إ', 'dirham': 'د.إ', 'uae dirham': 'د.إ',
      'sar': '﷼', 'riyal': '﷼', 'saudi riyal': '﷼',
      'jpy': '¥', 'yen': '¥',
      'cny': '¥', 'yuan': '¥', 'rmb': '¥',
      'aud': 'A$', 'australian dollar': 'A$',
      'cad': 'C$', 'canadian dollar': 'C$',
      'sgd': 'S$', 'singapore dollar': 'S$',
      'chf': 'CHF', 'swiss franc': 'CHF',
      'myr': 'RM', 'ringgit': 'RM',
      'thb': '฿', 'baht': '฿',
      'krw': '₩', 'won': '₩',
      'bdt': '৳', 'taka': '৳',
      'lkr': 'Rs', 'sri lankan rupee': 'Rs',
      'pkr': '₨', 'pakistani rupee': '₨',
      'npr': 'रू', 'nepalese rupee': 'रू',
      'zar': 'R', 'rand': 'R',
      'rub': '₽', 'ruble': '₽',
      'try': '₺', 'lira': '₺',
      'idr': 'Rp', 'rupiah': 'Rp',
      'php': '₱', 'peso': '₱',
      'vnd': '₫', 'dong': '₫',
      'ngn': '₦', 'naira': '₦',
      'egp': 'E£', 'egyptian pound': 'E£',
      'qar': 'QR', 'qatari riyal': 'QR',
      'kwd': 'KD', 'kuwaiti dinar': 'KD',
      'bhd': 'BD', 'bahraini dinar': 'BD',
      'omr': 'OMR', 'omani rial': 'OMR'
    };
    // Exact match first
    if (symbolMap[name]) return symbolMap[name];
    // Partial match - check if name contains any key
    for (const [key, symbol] of Object.entries(symbolMap)) {
      if (name.includes(key)) return symbol;
    }
    return supplierCurrencyName;
  }, [supplierCurrencyName]);

  const formatCurrency = useCallback((amt) => `${currencySymbol}${(amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, [currencySymbol]);

  // Styling
  const disabledFieldStyle = {
    '& .MuiInputBase-input.Mui-disabled': {
      color: '#5D4037',
      fontWeight: 700,
      WebkitTextFillColor: '#5D4037'
    }
  };

  const compactInput = {
    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
    ...(isViewMode && disabledFieldStyle)
  };

  const compactInputRight = {
    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right' },
    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
    ...(isViewMode && disabledFieldStyle)
  };

  const tinyInput = {
    '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.8rem', textAlign: 'right' },
    minWidth: 0,
    ...(isViewMode && disabledFieldStyle)
  };

  const tinyInputCenter = {
    '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.8rem', textAlign: 'center' },
    minWidth: 0,
    ...(isViewMode && disabledFieldStyle)
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          onClose();
        }}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        disableEscapeKeyDown
        slotProps={{
          backdrop: {
            onClick: (e) => e.stopPropagation()
          }
        }}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 2, width: isMobile ? '100%' : '95vw', maxWidth: '1400px' } }}
      >
        <DialogTitle sx={{ py: 1, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: isInternationalPO ? '#1e3a5f' : '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: isInternationalPO ? '#fff' : '#1e40af', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isInternationalPO ? '#fff' : '#1e293b', fontSize: isMobile ? '0.9rem' : '1rem' }}>{title}</Typography>
              {isInternationalPO && (
                <Chip label="International PO" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#3b82f6', color: '#fff', '& .MuiChip-label': { px: 1 } }} />
              )}
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ p: 0.5, color: isInternationalPO ? '#fff' : undefined }}><Close fontSize="small" /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 1.5 : 2, bgcolor: '#fff', position: 'relative' }}>
          {modalLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <Typography variant="body1" sx={{ color: '#6b7280' }}>Loading Purchase Order...</Typography>
            </Box>
          ) : (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 1, display: 'block' }}>Order Information</Typography>
              <Grid container spacing={1} sx={{ mb: 1.5 }}>
                <Grid item xs={6} sm={2}>
                  <TextField fullWidth size="small" label="PO Number" value={formData.PurchaseOrderNo || ''}
                    disabled={true} sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: '#f0f9ff' } }} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField fullWidth size="small" type="date" label="PO Date" value={formData.PurchaseOrderDate || ''}
                    onChange={(e) => handleChange('PurchaseOrderDate', e.target.value)} disabled={isViewMode}
                    InputLabelProps={{ shrink: true }} sx={compactInput} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  {isViewMode ? (
                    <TextField fullWidth size="small" label="Supplier" value={supplierDisplayName || '-'}
                      disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FormControl fullWidth size="small" sx={compactInput}>
                        <InputLabel>Supplier</InputLabel>
                        <Select value={formData.SupplierId || ''} onChange={(e) => handleChange('SupplierId', e.target.value)}
                          disabled={isViewMode} label="Supplier" sx={{ fontSize: '0.8rem' }}
                          MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}>
                          <MenuItem value="" disabled sx={{ fontSize: '0.75rem', py: 0.5 }}>
                            {Array.isArray(suppliers) && suppliers.length > 0 ? 'Select Supplier' : 'No suppliers available'}
                          </MenuItem>
                          {Array.isArray(suppliers) && suppliers.map(s => <MenuItem key={s.MasterId || s.Id} value={s.MasterId || s.Id} sx={{ fontSize: '0.75rem', py: 0.5 }}>{s.Name}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <Tooltip title="Add New Supplier">
                        <IconButton size="small" onClick={handleOpenVendorModal}
                          sx={{ bgcolor: '#1e3a5f', color: '#fff', borderRadius: '6px', width: 30, height: 30, flexShrink: 0,
                            '&:hover': { bgcolor: '#2d5a8e' } }}>
                          <PersonAdd sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={6} sm={2}>
                  {isViewMode ? (
                    <TextField fullWidth size="small" label="Currency" value={supplierCurrencyName ? `${supplierCurrencyName} (${currencySymbol})` : '-'} disabled
                      sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: supplierCurrencyName ? '#e0f2fe' : '#f9fafb' } }} />
                  ) : (
                    <Autocomplete
                      options={Array.isArray(currencies) ? currencies : []}
                      getOptionLabel={(option) => option?.Currency || ''}
                      isOptionEqualToValue={(option, val) => String(option?.Id) === String(val?.Id)}
                      value={(() => {
                        if (!Array.isArray(currencies) || currencies.length === 0) {
                          // Currencies not loaded yet - show a temporary object so field isn't blank
                          if (formData.CurrencyName) {
                            return { Id: formData.CurrencyTypeId, Currency: formData.CurrencyName };
                          }
                          return null;
                        }
                        if (formData.CurrencyTypeId) {
                          const byId = currencies.find(c => String(c.Id) === String(formData.CurrencyTypeId));
                          if (byId) return byId;
                        }
                        if (formData.CurrencyName) {
                          const byName = currencies.find(c => c.Currency && c.Currency.toLowerCase().trim() === formData.CurrencyName.toLowerCase().trim());
                          if (byName) return byName;
                          // No match in currencies list but formData has a name - show it anyway
                          return { Id: formData.CurrencyTypeId, Currency: formData.CurrencyName };
                        }
                        return null;
                      })()}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setFormData(prev => ({
                            ...prev,
                            CurrencyTypeId: newValue.Id,
                            CurrencyName: newValue.Currency || ''
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            CurrencyTypeId: null,
                            CurrencyName: ''
                          }));
                        }
                      }}
                      disabled={currencyLoading}
                      loading={currencyLoading}
                      ListboxProps={{
                        style: { fontSize: '0.8rem', maxHeight: '200px' }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Currency"
                          size="small"
                          placeholder="Select"
                          sx={compactInput}
                          InputProps={{
                            ...params.InputProps,
                            style: { fontSize: '0.8rem' },
                            endAdornment: (
                              <>
                                {currencyLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      sx={{
                        '& .MuiInputBase-root': { minHeight: '28px', py: '0 !important' },
                        '& .MuiInputBase-input': { py: '0.4 !important', fontSize: '0.8rem' },
                        '& .MuiAutocomplete-input': { py: '3.5px !important' },
                        '& .MuiOutlinedInput-root': { minHeight: '28px', padding: '0 9px !important' },
                        '& .MuiAutocomplete-endAdornment': { top: 'calc(50% - 10px)' }
                      }}
                    />
                  )}
                </Grid>
              </Grid>

              {/* Shipping & Delivery Details */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 1, mt: 1.5, display: 'block' }}>Shipping & Delivery Details</Typography>
              <Grid container spacing={1} sx={{ mb: 1.5 }}>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth size="small" label="Port of Loading" value={formData.PortOfLoading || ''}
                    onChange={(e) => handleChange('PortOfLoading', e.target.value)} disabled={isViewMode} sx={compactInput} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth size="small" label="Port of Discharge" value={formData.PortOfDischarge || ''}
                    onChange={(e) => handleChange('PortOfDischarge', e.target.value)} disabled={isViewMode} sx={compactInput} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size="small" sx={compactInput}>
                    <InputLabel>Mode of Shipment</InputLabel>
                    <Select value={formData.ModeOfShipment || ''} onChange={(e) => handleChange('ModeOfShipment', e.target.value)}
                      disabled={isViewMode} label="Mode of Shipment" sx={{ fontSize: '0.8rem' }}>
                      <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Select</MenuItem>
                      <MenuItem value="Sea" sx={{ fontSize: '0.75rem' }}>Sea</MenuItem>
                      <MenuItem value="Air" sx={{ fontSize: '0.75rem' }}>Air</MenuItem>
                      <MenuItem value="Road" sx={{ fontSize: '0.75rem' }}>Road</MenuItem>
                      <MenuItem value="Rail" sx={{ fontSize: '0.75rem' }}>Rail</MenuItem>
                      <MenuItem value="Courier" sx={{ fontSize: '0.75rem' }}>Courier</MenuItem>
                      <MenuItem value="Multi-Modal" sx={{ fontSize: '0.75rem' }}>Multi-Modal</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size="small" sx={compactInput}>
                    <InputLabel>Partial Shipment</InputLabel>
                    <Select value={formData.PartialShipment || ''} onChange={(e) => handleChange('PartialShipment', e.target.value)}
                      disabled={isViewMode} label="Partial Shipment" sx={{ fontSize: '0.8rem' }}>
                      <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Select</MenuItem>
                      <MenuItem value="Allowed" sx={{ fontSize: '0.75rem' }}>Allowed</MenuItem>
                      <MenuItem value="Not Allowed" sx={{ fontSize: '0.75rem' }}>Not Allowed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size="small" sx={compactInput}>
                    <InputLabel>Transhipment</InputLabel>
                    <Select value={formData.Transhipment || ''} onChange={(e) => handleChange('Transhipment', e.target.value)}
                      disabled={isViewMode} label="Transhipment" sx={{ fontSize: '0.8rem' }}>
                      <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Select</MenuItem>
                      <MenuItem value="Allowed" sx={{ fontSize: '0.75rem' }}>Allowed</MenuItem>
                      <MenuItem value="Not Allowed" sx={{ fontSize: '0.75rem' }}>Not Allowed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth size="small" label="Incoterms" value={formData.Incoterms || ''}
                    onChange={(e) => handleChange('Incoterms', e.target.value)} disabled={isViewMode} sx={compactInput}
                    placeholder="e.g. FOB, CIF, EXW" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Payment Terms" value={formData.PaymentTerms || ''}
                    onChange={(e) => handleChange('PaymentTerms', e.target.value)} disabled={isViewMode} sx={compactInput} />
                </Grid>

                <Grid item xs={6} sm={4}>
                  <TextField fullWidth size="small" label="Delivery Location" value={formData.DeliveryLocation || ''}
                    onChange={(e) => handleChange('DeliveryLocation', e.target.value)} disabled={isViewMode} sx={compactInput} />
                </Grid>
                
                <Grid item xs={6} sm={2}>
                  <TextField fullWidth size="small" type="date" label="Requested Shipment Date" value={formData.RequestedShipmentDate || ''}
                    onChange={(e) => handleChange('RequestedShipmentDate', e.target.value)} disabled={isViewMode}
                    InputLabelProps={{ shrink: true }} sx={compactInput} />
                </Grid>
                
                <Grid item xs={6} sm={2}>
                  <TextField fullWidth size="small" type="date" label="Required Delivery Date" value={formData.RequiredDeliveryDate || ''}
                    onChange={(e) => handleChange('RequiredDeliveryDate', e.target.value)} disabled={isViewMode}
                    InputLabelProps={{ shrink: true }} sx={compactInput} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField fullWidth size="small" label="Requested By / Department" value={formData.RequestedBy || ''}
                    onChange={(e) => handleChange('RequestedBy', e.target.value)} disabled={isViewMode} sx={compactInput} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af' }}>Order Items</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {orderDetails.length > 0 && (
                    <Typography
                      variant="caption"
                      onClick={() => {
                        setItemsModalOpen(true);
                        setItemsModalPage(1);
                      }}
                      sx={{
                        color: '#1e40af',
                        cursor: 'pointer',
                        fontWeight: 500,
                        textDecoration: 'underline',
                        fontSize: '0.7rem',
                        '&:hover': { color: '#1d4ed8' }
                      }}
                    >
                      See More ({orderDetails.length} items)
                    </Typography>
                  )}
                  {!isViewMode && formData.SupplierId && modalAction === 'create' && (
                    <Button
                      size="small"
                      startIcon={<History sx={{ fontSize: 14 }} />}
                      onClick={() => fetchPOHistoryForSupplier(formData.SupplierId)}
                      variant="text"
                      sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#7c3aed' }}
                    >
                      History
                    </Button>
                  )}
                  {!isViewMode && (
                    <>
                      <Button
                        size="small"
                        startIcon={<List sx={{ fontSize: 14 }} />}
                        onClick={handleOpenGetItemsModal}
                        variant="text"
                        sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#059669' }}
                      >
                        Get Items
                      </Button>
                      <Button
                        size="small"
                        startIcon={<AddBox sx={{ fontSize: 14 }} />}
                        onClick={handleOpenQuickItemModal}
                        variant="text"
                        sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#1e3a5f' }}
                      >
                        New Item
                      </Button>
                      {/* <Button size="small" startIcon={<Add sx={{ fontSize: 16 }} />} onClick={addDetailRow}
                        variant="outlined" sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0 }}>Add</Button> */}
                    </>
                  )}
                </Box>
              </Box>

              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, mb: 1.5, overflow: 'hidden' }}>
                <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: 'linear-gradient(135deg, #324372 0%, #2a3a63 100%)' }}>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '3%' }}>#</th>
                        <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '20%' }}>Item</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>HSN</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '6%' }}>Qty</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '8%' }}>Rate</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '6%' }}>Disc%</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '8%' }}>DiscAmt</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%' }}>Taxable</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '6%' }}>VAT%</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '9%' }}>VAT Amt</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%' }}>Total</th>
                        {!isViewMode && <th style={{ padding: '8px 4px', width: '6%', background: 'linear-gradient(135deg, #324372 0%, #2a3a63 100%)', textAlign: 'right', fontWeight: 600, color: '#fff' }}>Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {orderDetails.slice(0, Math.min(50, orderDetails.length)).map((detail, index) => (
                        <TableRow
                          key={`${detail.Id}-${index}`}
                          detail={detail}
                          index={index}
                          items={items}
                          isViewMode={isViewMode}
                          onDetailChange={handleDetailChange}
                          onItemSelect={handleItemSelect}
                          onRemoveRow={removeDetailRow}
                          formatCurrency={formatCurrency}
                          disabledFieldStyle={disabledFieldStyle}
                          tinyInput={tinyInput}
                          tinyInputCenter={tinyInputCenter}
                        />
                      ))}
                      {orderDetails.length === 0 && (
                        <tr><td colSpan={isViewMode ? 10 : 11} style={{ textAlign: 'center', padding: '16px', color: '#6b7280' }}>No items added</td></tr>
                      )}
                      {orderDetails.length > 50 && (
                        <tr><td colSpan={isViewMode ? 10 : 11} style={{ textAlign: 'center', padding: '8px', color: '#1e40af', fontSize: '0.8rem', fontWeight: 500 }}>
                          Showing 50 of {orderDetails.length} items.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Grid container spacing={isMobile ? 1 : 2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 1, display: 'block' }}>Additional Charges</Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" type="number" label="Freight" value={formData.FreightAmount || ''}
                        onChange={(e) => handleChange('FreightAmount', e.target.value)} disabled={isViewMode} sx={compactInputRight} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" type="number" label="Packing" value={formData.PackingAmount || ''}
                        onChange={(e) => handleChange('PackingAmount', e.target.value)} disabled={isViewMode} sx={compactInputRight} />
                    </Grid>
                    {/* <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Instruction 1" value={formData.Instruction1 || ''}
                        onChange={(e) => handleChange('Instruction1', e.target.value)} disabled={isViewMode} multiline rows={1} sx={compactInput} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Instruction 2" value={formData.Instruction2 || ''}
                        onChange={(e) => handleChange('Instruction2', e.target.value)} disabled={isViewMode} multiline rows={1} sx={compactInput} />
                    </Grid> */}
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Remarks" value={formData.Remarks || ''}
                        onChange={(e) => handleChange('Remarks', e.target.value)} disabled={isViewMode} multiline rows={2} sx={compactInput} />
                    </Grid>
                    {/* Audit Information - under Remarks */}
                    {(modalAction === 'view' || modalAction === 'edit') && (
                      <Grid item xs={12} sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                          <span style={{ display: 'block', marginBottom: '4px' }}>
                            <span style={{ color: '#64748b' }}>Created by </span>
                            <span style={{ color: '#1e40af', fontWeight: 600 }}>{formData.CreatedByName || '-'}</span>
                            <span style={{ color: '#64748b' }}> on </span>
                            <span style={{ color: '#059669', fontWeight: 500 }}>{formData.CreatedDate ? new Date(formData.CreatedDate).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                          </span>
                          <span style={{ display: 'block' }}>
                            <span style={{ color: '#64748b' }}>Modified by </span>
                            <span style={{ color: '#dc2626', fontWeight: 600 }}>{formData.ModifiedByName || '-'}</span>
                            <span style={{ color: '#64748b' }}> on </span>
                            <span style={{ color: '#f59e0b', fontWeight: 500 }}>{formData.ModifiedDate ? new Date(formData.ModifiedDate).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                          </span>
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 0.5, display: 'block' }}>Order Summary</Typography>
                  <Box sx={{ bgcolor: '#f8fafc', borderRadius: 1, p: 1.5, border: '1px solid #e2e8f0' }}>
                    {[
                      { label: 'Sub Total', value: formData.Amount },
                      { label: 'Discount', value: formData.DiscountAmount, isNeg: true },
                      { label: 'Taxable', value: formData.TaxableAmount },
                      { label: 'VAT', value: formData.VATAmount },
                      ...(parseFloat(formData.FreightAmount) > 0 ? [
                        { label: 'Freight', value: parseFloat(formData.FreightAmount) || 0 },
                        { label: `Freight VAT (${formData.FreightVATRate || 0}%)`, value: parseFloat(formData.FreightVATAmount) || 0 },
                      ] : []),
                      ...(parseFloat(formData.PackingAmount) > 0 ? [
                        { label: 'Packing', value: parseFloat(formData.PackingAmount) || 0 },
                        { label: `Packing VAT (${formData.PackingVATRate || 0}%)`, value: parseFloat(formData.PackingVATAmount) || 0 },
                      ] : []),
                      { label: 'Round Off', value: formData.RoundOff },
                    ].map((row, i) => (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: i === 5 ? '1px solid #d1d5db' : 'none' }}>
                        <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.85rem' }}>{row.label}:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: row.isNeg ? '#dc2626' : '#374151', fontSize: '0.85rem' }}>
                          {row.isNeg ? '- ' : ''}{formatCurrency(row.value)}
                        </Typography>
                      </Box>
                    ))}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, mt: 1, borderTop: '2px solid #1e40af' }}>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e40af', fontSize: '1rem' }}>Grand Total:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e40af', fontSize: '1.1rem' }}>{formatCurrency(formData.TotalAmount)}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}

          {/* Terms & Conditions Display - View Mode: labels only, Edit/Create Mode: with checkboxes */}
          {isViewMode && formData.SelectedTermsData && formData.SelectedTermsData.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {/* Local T&C */}
              {formData.SelectedTermsData.filter(t => !t.IsInternational).length > 0 && (
                <Box sx={{ p: 1.5, bgcolor: '#fffbeb', borderRadius: 1, border: '1px solid #fde68a', mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400e', mb: 1, display: 'block', fontSize: '0.8rem' }}>
                    📋 Terms & Conditions ({formData.SelectedTermsData.filter(t => !t.IsInternational).length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {formData.SelectedTermsData.filter(t => !t.IsInternational).map((term, index) => (
                      <Box key={term.Id || index} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 700, minWidth: 16 }}>
                          {index + 1}.
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#78350f', fontSize: '0.75rem', lineHeight: 1.4 }}>
                          {term.Name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              {/* International PO T&C */}
              {formData.SelectedTermsData.filter(t => t.IsInternational).length > 0 && (
                <Box sx={{ p: 1.5, bgcolor: '#eff6ff', borderRadius: 1, border: '1px solid #93c5fd' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', fontSize: '0.8rem' }}>
                      📋 International PO T&C ({formData.SelectedTermsData.filter(t => t.IsInternational).length})
                    </Typography>
                    <Chip label="International PO T&C" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, bgcolor: '#1e40af', color: '#fff', '& .MuiChip-label': { px: 0.75 } }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {formData.SelectedTermsData.filter(t => t.IsInternational).map((term, index) => (
                      <Box key={term.Id || index} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="caption" sx={{ color: '#1d4ed8', fontWeight: 700, minWidth: 16 }}>
                          {index + 1}.
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#1e3a5f', fontSize: '0.75rem', lineHeight: 1.4 }}>
                          {term.Name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Terms & Conditions - Edit/Create Mode: inline checkboxes */}
          {!isViewMode && (
            <Box sx={{ mt: 2 }}>
              {tcLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, p: 1.5, bgcolor: '#fffbeb', borderRadius: 1, border: '1px solid #fde68a' }}>
                  <CircularProgress size={16} sx={{ color: '#d97706' }} />
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Loading...</Typography>
                </Box>
              ) : tcData.length === 0 ? (
                <Box sx={{ p: 1.5, bgcolor: '#fffbeb', borderRadius: 1, border: '1px solid #fde68a' }}>
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>No terms available</Typography>
                </Box>
              ) : (
                <>
                  {/* T&C Search */}
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search terms & conditions..."
                    value={tcSearchValue}
                    onChange={(e) => setTcSearchValue(e.target.value)}
                    sx={{ mb: 1, '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ fontSize: 16, color: '#9ca3af' }} />
                        </InputAdornment>
                      )
                    }}
                  />

                  {/* Local T&C */}
                  {tcData.filter(t => !t.IsInternational).length > 0 && (
                    <Box sx={{ p: 1.5, bgcolor: '#fffbeb', borderRadius: 1, border: '1px solid #fde68a', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400e', fontSize: '0.8rem' }}>
                          📋 Terms & Conditions ({tcData.filter(t => !t.IsInternational).filter(t => selectedTerms.includes(t.Id)).length}/{tcData.filter(t => !t.IsInternational).length})
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 150, overflow: 'auto' }}>
                        {tcData.filter(t => !t.IsInternational).filter(t => !tcSearchValue || t.Name?.toLowerCase().includes(tcSearchValue.toLowerCase())).map((term, index) => {
                          const isSelected = selectedTerms.includes(term.Id);
                          return (
                            <Box
                              key={term.Id}
                              onClick={() => handleToggleTerm(term.Id)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                py: 0.5,
                                px: 1,
                                borderRadius: 0.5,
                                cursor: 'pointer',
                                bgcolor: isSelected ? '#fef3c7' : 'transparent',
                                border: isSelected ? '1px solid #f59e0b' : '1px solid transparent',
                                '&:hover': { bgcolor: isSelected ? '#fde68a' : '#fefce8' }
                              }}
                            >
                              <Checkbox
                                checked={isSelected}
                                size="small"
                                sx={{ p: 0, color: '#d97706', '&.Mui-checked': { color: '#d97706' } }}
                              />
                              <Typography variant="caption" sx={{ color: '#78350f', fontSize: '0.75rem', lineHeight: 1.4 }}>
                                {index + 1}. {term.Name}
                              </Typography>
                            </Box>
                          );
                        })}
                        {tcData.filter(t => !t.IsInternational).filter(t => !tcSearchValue || t.Name?.toLowerCase().includes(tcSearchValue.toLowerCase())).length === 0 && tcSearchValue && (
                          <Typography variant="caption" sx={{ color: '#9ca3af', py: 0.5, px: 1 }}>No matching terms</Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* International PO T&C */}
                  {tcData.filter(t => t.IsInternational).length > 0 && (
                    <Box sx={{ p: 1.5, bgcolor: '#eff6ff', borderRadius: 1, border: '1px solid #93c5fd' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', fontSize: '0.8rem' }}>
                            📋 International PO T&C ({tcData.filter(t => t.IsInternational).filter(t => selectedTerms.includes(t.Id)).length}/{tcData.filter(t => t.IsInternational).length})
                          </Typography>
                          <Chip label="International PO T&C" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, bgcolor: '#1e40af', color: '#fff', '& .MuiChip-label': { px: 0.75 } }} />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 150, overflow: 'auto' }}>
                        {tcData.filter(t => t.IsInternational).filter(t => !tcSearchValue || t.Name?.toLowerCase().includes(tcSearchValue.toLowerCase())).map((term, index) => {
                          const isSelected = selectedTerms.includes(term.Id);
                          return (
                            <Box
                              key={term.Id}
                              onClick={() => handleToggleTerm(term.Id)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                py: 0.5,
                                px: 1,
                                borderRadius: 0.5,
                                cursor: 'pointer',
                                bgcolor: isSelected ? '#dbeafe' : 'transparent',
                                border: isSelected ? '1px solid #3b82f6' : '1px solid transparent',
                                '&:hover': { bgcolor: isSelected ? '#bfdbfe' : '#eff6ff' }
                              }}
                            >
                              <Checkbox
                                checked={isSelected}
                                size="small"
                                sx={{ p: 0, color: '#1e40af', '&.Mui-checked': { color: '#1e40af' } }}
                              />
                              <Typography variant="caption" sx={{ color: '#1e3a5f', fontSize: '0.75rem', lineHeight: 1.4 }}>
                                {index + 1}. {term.Name}
                              </Typography>
                            </Box>
                          );
                        })}
                        {tcData.filter(t => t.IsInternational).filter(t => !tcSearchValue || t.Name?.toLowerCase().includes(tcSearchValue.toLowerCase())).length === 0 && tcSearchValue && (
                          <Typography variant="caption" sx={{ color: '#9ca3af', py: 0.5, px: 1 }}>No matching terms</Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', gap: 1 }}>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={onClose}
            variant="outlined"
            size="small"
            sx={{
              borderColor: '#d1d5db',
              color: '#374151',
              fontSize: '0.8rem',
              px: 2,
              py: 0.75,
              textTransform: 'none',
              '&:hover': { borderColor: '#9ca3af', bgcolor: '#f3f4f6' }
            }}
          >
            {isViewMode ? 'Close' : 'Cancel'}
          </Button>
          {!isViewMode && (
            <Button
              onClick={onSave}
              variant="contained"
              size="small"
              disabled={loading || !formData.SupplierId || orderDetails.length === 0}
              sx={{
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                color: '#fff',
                fontSize: '0.8rem',
                px: 2,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb, #1e40af)',
                  boxShadow: '0 4px 8px rgba(59, 130, 246, 0.4)'
                },
                '&:disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af'
                }
              }}
            >
              {modalAction === 'create' ? 'Create Order' : 'Update Order'}
            </Button>
          )}
          {isViewMode && !formData.IsVerified && !formData.IsConverted && (
            <Button
              onClick={() => setVerifyDialogOpen(true)}
              variant="contained"
              size="small"
              disabled={loading}
              startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
              sx={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: '#fff',
                fontSize: '0.8rem',
                px: 2,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669, #047857)',
                  boxShadow: '0 4px 8px rgba(16, 185, 129, 0.4)'
                },
                '&:disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af'
                }
              }}
            >
              Verify
            </Button>
          )}
          {isViewMode && formData.IsVerified && !formData.IsConverted && (
            <Button
              onClick={() => setConvertDialogOpen(true)}
              variant="contained"
              size="small"
              disabled={loading}
              startIcon={<SwapHoriz sx={{ fontSize: 16 }} />}
              sx={{
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                color: '#fff',
                fontSize: '0.8rem',
                px: 2,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(139, 92, 246, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                  boxShadow: '0 4px 8px rgba(139, 92, 246, 0.4)'
                },
                '&:disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af'
                }
              }}
            >
              Convert to Purchase
            </Button>
          )}
        </DialogActions>

      </Dialog>

      {/* Verify Confirmation Dialog */}
      <Dialog
        open={verifyDialogOpen}
        onClose={() => setVerifyDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>Verify Purchase Order</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
            Are you sure you want to verify this Purchase Order?
          </Typography>
          <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 500 }}>
            Note: Once verified, this order cannot be modified.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setVerifyDialogOpen(false)} size="small" variant="outlined">Cancel</Button>
          <Button onClick={() => { onVerify(formData.Id); setVerifyDialogOpen(false); }} size="small" variant="contained"
            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>Verify</Button>
        </DialogActions>
      </Dialog>

      {/* Convert Confirmation Dialog */}
      <Dialog
        open={convertDialogOpen}
        onClose={() => setConvertDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHoriz sx={{ color: '#8b5cf6', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>Convert to Purchase</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Are you sure you want to convert this Purchase Order to Purchase?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConvertDialogOpen(false)} size="small" variant="outlined">Cancel</Button>
          <Button onClick={() => { onConvert(formData.Id); setConvertDialogOpen(false); }} size="small" variant="contained"
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}>Convert</Button>
        </DialogActions>
      </Dialog>

      {/* See More Items Modal */}
      <Dialog
        open={itemsModalOpen}
        onClose={() => setItemsModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', m: 2, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <List sx={{ color: '#1e40af', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                Order Items ({orderDetails.length} total)
              </Typography>
            </Box>
            <IconButton onClick={() => setItemsModalOpen(false)} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: 'linear-gradient(135deg, #324372 0%, #2a3a63 100%)' }}>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '5%' }}>#</th>
                    <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '25%' }}>Item</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%' }}>HSN</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>Qty</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%' }}>Rate</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>Disc%</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%' }}>Taxable</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>VAT%</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%' }}>VAT Amt</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '12%' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {getPaginatedOrderDetails.map((detail, index) => {
                    const actualIndex = (itemsModalPage - 1) * itemsModalPerPage + index;
                    return (
                      <tr key={actualIndex} style={{ borderBottom: '1px solid #f1f5f9', background: index % 2 === 0 ? '#fff' : '#f9fafb' }}>
                        <td style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 500 }}>{actualIndex + 1}</td>
                        <td style={{ padding: '8px 4px', fontWeight: 500 }}>
                          {detail.ItemName || items.find(i => i.Id === parseInt(detail.ItemId))?.Name || '-'}
                        </td>
                        <td style={{ padding: '8px 4px', textAlign: 'center' }}>{detail.HSNCode || '-'}</td>
                        <td style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 500 }}>{detail.Quantity || 0}</td>
                        <td style={{ padding: '8px 4px', textAlign: 'right' }}>{formatCurrency(detail.Rate)}</td>
                        <td style={{ padding: '8px 4px', textAlign: 'center' }}>{detail.DiscountPercentage || 0}%</td>
                        <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(detail.TaxableAmount)}</td>
                        <td style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 500 }}>{detail.VATRate || 0}%</td>
                        <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 700, color: '#1B5E20' }}>
                          {formatCurrency(parseFloat(detail.VATAmount) || 0)}
                        </td>
                        <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
                          {formatCurrency(detail.TotalAmount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          </Box>
          {getOrderDetailsTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={getOrderDetailsTotalPages}
                page={itemsModalPage}
                onChange={(e, page) => setItemsModalPage(page)}
                size="small"
                color="primary"
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Get Items Modal */}
      <Dialog
        open={getItemsModalOpen}
        onClose={handleCloseGetItemsModal}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px', m: isMobile ? 0 : 2, maxHeight: '95vh' } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <List sx={{ color: '#059669', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                Get Items ({filteredItems.length} available)
              </Typography>
            </Box>
            <IconButton onClick={handleCloseGetItemsModal} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {getItemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2, color: '#6b7280' }}>Loading items...</Typography>
            </Box>
          ) : (
            <>
              {/* Search */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search items by name or HSN code..."
                  value={getItemsSearchValue}
                  onChange={(e) => handleGetItemsSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: '#6b7280' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{ '& .MuiInputBase-input': { py: 1 } }}
                />
              </Box>

              {/* Selected Items Summary */}
              {selectedItems.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f0f9ff', borderRadius: 1, border: '1px solid #bfdbfe' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 1, display: 'block' }}>
                    Selected Items ({selectedItems.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedItems.slice(0, 10).map(item => (
                      <Chip
                        key={item.Id}
                        label={`${item.Name} (${item.selectedQuantity})`}
                        size="small"
                        onDelete={() => handleItemSelection(item, false)}
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ))}
                    {selectedItems.length > 10 && (
                      <Chip label={`+${selectedItems.length - 10} more`} size="small" variant="outlined" />
                    )}
                  </Box>
                </Box>
              )}

              {/* Items Table */}
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '5%' }}>
                          <Checkbox
                            size="small"
                            checked={selectedItems.length === getPaginatedItems.length && getPaginatedItems.length > 0}
                            indeterminate={selectedItems.length > 0 && selectedItems.length < getPaginatedItems.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                getPaginatedItems.forEach(item => {
                                  if (!selectedItems.find(si => si.Id === item.Id)) {
                                    handleItemSelection(item, true);
                                  }
                                });
                              } else {
                                getPaginatedItems.forEach(item => {
                                  handleItemSelection(item, false);
                                });
                              }
                            }}
                            sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }}
                          />
                        </th>
                        <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '25%' }}>Item</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%' }}>HSN</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>VAT%</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>Qty</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%' }}>Rate</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>Disc%</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%' }}>Disc Amt</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '12%' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedItems.map((item, index) => {
                        const selectedItem = selectedItems.find(si => si.Id === item.Id);
                        const isSelected = !!selectedItem;
                        const isAlreadyAdded = isItemAlreadyInOrder(item.Id);

                        return (
                          <tr key={item.Id} style={{
                            borderBottom: '1px solid #f1f5f9',
                            background: isSelected ? '#f0f9ff' : (index % 2 === 0 ? '#fff' : '#f9fafb'),
                            opacity: isAlreadyAdded ? 0.5 : 1
                          }}>
                            <td style={{ padding: '4px', textAlign: 'center' }}>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={isAlreadyAdded}
                                onChange={(e) => handleItemSelection(item, e.target.checked)}
                              />
                            </td>
                            <td style={{ padding: '4px', fontWeight: 500 }}>
                              {item.Name}
                              {isAlreadyAdded && (
                                <Chip label="Added" size="small" color="success" sx={{ ml: 1, fontSize: '0.6rem', height: 16 }} />
                              )}
                            </td>
                            <td style={{ padding: '4px', textAlign: 'center' }}>{item.HSNCode || '-'}</td>
                            <td style={{ padding: '4px', textAlign: 'center' }}>{item.VATRate || item.GSTRate || 0}%</td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedQuantity}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                  inputProps={{ min: 1 }}
                                  sx={{ width: 60, '& .MuiInputBase-input': { py: 0.25, px: 0.5, fontSize: '0.8rem', textAlign: 'center' } }}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af' }}>-</span>
                              )}
                            </td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedRate}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedRate', e.target.value)}
                                  inputProps={{ min: 0 }}
                                  sx={{ width: 80, '& .MuiInputBase-input': { py: 0.25, px: 0.5, fontSize: '0.8rem', textAlign: 'right' } }}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'right', color: '#9ca3af' }}>
                                  {formatCurrency(item.PurchaseRate || item.SaleRate || 0)}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedDiscountPercentage}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountPercentage', e.target.value)}
                                  inputProps={{ min: 0, max: 100 }}
                                  sx={{ width: 60, '& .MuiInputBase-input': { py: 0.25, px: 0.5, fontSize: '0.8rem', textAlign: 'center' } }}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af' }}>0</span>
                              )}
                            </td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedDiscountAmount}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountAmount', e.target.value)}
                                  inputProps={{ min: 0 }}
                                  sx={{ width: 80, '& .MuiInputBase-input': { py: 0.25, px: 0.5, fontSize: '0.8rem', textAlign: 'right' } }}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'right', color: '#9ca3af' }}>-</span>
                              )}
                            </td>
                            <td style={{ padding: '4px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
                              {isSelected ? (
                                (() => {
                                  const qty = parseFloat(selectedItem.selectedQuantity) || 0;
                                  const rate = parseFloat(selectedItem.selectedRate) || 0;
                                  const discPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
                                  const discAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;
                                  const amount = qty * rate;
                                  const finalDiscAmt = discAmt > 0 ? discAmt : (amount * discPct) / 100;
                                  const taxable = amount - finalDiscAmt;
                                  const vatAmt = (taxable * (item.VATRate || item.GSTRate || 0)) / 100;
                                  return formatCurrency(taxable + vatAmt);
                                })()
                              ) : (
                                <span style={{ color: '#9ca3af' }}>-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Pagination */}
              {getTotalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={getTotalPages}
                    page={currentItemsPage}
                    onChange={(e, page) => setCurrentItemsPage(page)}
                    size="small"
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Button onClick={handleCloseGetItemsModal} variant="outlined" size="small">Cancel</Button>
          <Button
            onClick={handleAddSelectedItems}
            variant="contained"
            size="small"
            disabled={selectedItems.length === 0 || addingItemsLoading}
            sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}
          >
            {addingItemsLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Adding...
              </>
            ) : (
              `Add Selected (${selectedItems.length})`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PO History Modal */}
      <Dialog
        open={poHistoryModalOpen}
        onClose={closeHistoryModal}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px', m: isMobile ? 0 : 2, maxHeight: '95vh' } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <History sx={{ color: '#7c3aed', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                Purchase Order History
              </Typography>
            </Box>
            <IconButton onClick={closeHistoryModal} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {poHistoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2, color: '#6b7280' }}>Loading history...</Typography>
            </Box>
          ) : poHistoryData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: '#6b7280' }}>
              <Typography variant="body2">No purchase order history found for this supplier.</Typography>
            </Box>
          ) : (
            <>
              {/* Selected Items Summary */}
              {selectedHistoryItems.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fef3c7', borderRadius: 1, border: '1px solid #fbbf24' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400e', mb: 1, display: 'block' }}>
                    Selected Items ({selectedHistoryItems.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedHistoryItems.slice(0, 10).map((item, index) => (
                      <Chip
                        key={`${item.poId}-${item.ItemId}-${index}`}
                        label={`${items.find(i => i.Id === parseInt(item.ItemId))?.Name || 'Unknown'} (${item.Quantity})`}
                        size="small"
                        onDelete={() => toggleHistoryItemSelection(item, item.poId)}
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ))}
                    {selectedHistoryItems.length > 10 && (
                      <Chip label={`+${selectedHistoryItems.length - 10} more`} size="small" variant="outlined" />
                    )}
                  </Box>
                </Box>
              )}

              {/* History List */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Found {poHistoryData.length} previous orders
                </Typography>
                <Button
                  size="small"
                  onClick={toggleSelectAllHistoryItems}
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                >
                  {selectedHistoryItems.length === getAllHistoryItems().length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>

              {poHistoryData.map((po) => (
                <Box key={po.Id} sx={{ mb: 2, border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                  {/* PO Header */}
                  <Box sx={{
                    p: 1.5,
                    bgcolor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Checkbox
                        size="small"
                        checked={isPOFullySelected(po.Id)}
                        indeterminate={isPOPartiallySelected(po.Id)}
                        onChange={() => togglePOSelection(po.Id)}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                          PO #{po.PurchaseOrderNo}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          Date: {new Date(po.PurchaseOrderDate).toLocaleDateString('en-IN')} |
                          Items: {(po.PurchaseOrderDetails || []).length} |
                          Total: {formatCurrency(po.TotalAmount)}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => setExpandedHistoryPOId(expandedHistoryPOId === po.Id ? null : po.Id)}
                    >
                      {expandedHistoryPOId === po.Id ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>

                  {/* PO Items */}
                  <Collapse in={expandedHistoryPOId === po.Id}>
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                          <tr style={{ background: '#f1f5f9' }}>
                            <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600, color: '#374151', width: '5%' }}>
                              <Checkbox
                                size="small"
                                checked={isPOFullySelected(po.Id)}
                                indeterminate={isPOPartiallySelected(po.Id)}
                                onChange={() => togglePOSelection(po.Id)}
                              />
                            </th>
                            <th style={{ padding: '6px 4px', textAlign: 'left', fontWeight: 600, color: '#374151', width: '25%' }}>Item</th>
                            <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600, color: '#374151', width: '8%' }}>Qty</th>
                            <th style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 600, color: '#374151', width: '10%' }}>Rate</th>
                            <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600, color: '#374151', width: '8%' }}>Disc%</th>
                            <th style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 600, color: '#374151', width: '10%' }}>Taxable</th>
                            <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600, color: '#374151', width: '8%' }}>VAT%</th>
                            <th style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 600, color: '#374151', width: '12%' }}>Total</th>
                            <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600, color: '#374151', width: '10%' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(po.PurchaseOrderDetails || []).map((historyItem, index) => {
                            const isSelected = isHistoryItemSelected(historyItem, po.Id);
                            const isAlreadyAdded = isItemAlreadyInOrder(historyItem.ItemId);

                            return (
                              <tr key={`${po.Id}-${historyItem.ItemId}-${index}`} style={{
                                borderBottom: '1px solid #f1f5f9',
                                background: isSelected ? '#fef3c7' : (index % 2 === 0 ? '#fff' : '#f9fafb'),
                                opacity: isAlreadyAdded ? 0.6 : 1
                              }}>
                                <td style={{ padding: '4px', textAlign: 'center' }}>
                                  <Checkbox
                                    size="small"
                                    checked={isSelected}
                                    disabled={isAlreadyAdded}
                                    onChange={() => toggleHistoryItemSelection(historyItem, po.Id)}
                                  />
                                </td>
                                <td style={{ padding: '4px', fontWeight: 500 }}>
                                  {items.find(i => i.Id === parseInt(historyItem.ItemId))?.Name || 'Unknown Item'}
                                  {isAlreadyAdded && (
                                    <Chip label="Added" size="small" color="success" sx={{ ml: 1, fontSize: '0.6rem', height: 16 }} />
                                  )}
                                </td>
                                <td style={{ padding: '4px', textAlign: 'center', fontWeight: 500 }}>{historyItem.Quantity}</td>
                                <td style={{ padding: '4px', textAlign: 'right' }}>{formatCurrency(historyItem.Rate)}</td>
                                <td style={{ padding: '4px', textAlign: 'center' }}>{historyItem.DiscountPercentage || 0}%</td>
                                <td style={{ padding: '4px', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(historyItem.TaxableAmount)}</td>
                                <td style={{ padding: '4px', textAlign: 'center', fontWeight: 500 }}>{historyItem.VATRate || historyItem.GSTRate || 0}%</td>
                                <td style={{ padding: '4px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
                                  {formatCurrency(historyItem.TotalAmount)}
                                </td>
                                <td style={{ padding: '4px', textAlign: 'center' }}>
                                  {isAlreadyAdded ? (
                                    <Chip label="In Order" size="small" color="success" sx={{ fontSize: '0.6rem' }} />
                                  ) : isSelected ? (
                                    <Chip label="Selected" size="small" color="warning" sx={{ fontSize: '0.6rem' }} />
                                  ) : (
                                    <Chip label="Available" size="small" variant="outlined" sx={{ fontSize: '0.6rem' }} />
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Button onClick={closeHistoryModal} variant="outlined" size="small">Cancel</Button>
          <Button
            onClick={addSelectedHistoryItems}
            variant="contained"
            size="small"
            disabled={selectedHistoryItems.length === 0}
            sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}
          >
            Add Selected ({selectedHistoryItems.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Terms & Conditions Modal */}
      <Dialog
        open={tcModalOpen}
        onClose={handleCloseTcModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px', maxHeight: '80vh' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#fffbeb' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#92400e', fontSize: '0.95rem' }}>
                📋 Terms & Conditions
              </Typography>
            </Box>
            <IconButton onClick={handleCloseTcModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {tcLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: '#d97706' }} />
              <Typography variant="body2" sx={{ ml: 2, color: '#6b7280' }}>Loading terms...</Typography>
            </Box>
          ) : tcData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body2" sx={{ color: '#9ca3af' }}>No terms and conditions found</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>
                  {selectedTerms.length} of {tcData.length} selected
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAllTerms}
                  sx={{ 
                    textTransform: 'none', 
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1.5,
                    borderColor: '#d97706',
                    color: '#d97706',
                    '&:hover': { borderColor: '#b45309', bgcolor: '#fffbeb' }
                  }}
                >
                  {selectedTerms.length === tcData.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>
              <Box sx={{ maxHeight: 400, overflow: 'auto', px: 2, py: 1 }}>
                {tcData.map((term, index) => {
                  const isSelected = selectedTerms.includes(term.Id);
                  return (
                    <Box
                      key={term.Id}
                      onClick={() => handleToggleTerm(term.Id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        p: 1.5,
                        mb: 1,
                        borderRadius: 1,
                        border: isSelected ? '1px solid #f59e0b' : '1px solid #e5e7eb',
                        bgcolor: isSelected ? '#fffbeb' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: isSelected ? '#fef3c7' : '#f9fafb',
                          borderColor: '#f59e0b'
                        }
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        size="small"
                        sx={{ 
                          p: 0, 
                          mt: 0.25,
                          color: '#d97706',
                          '&.Mui-checked': { color: '#d97706' }
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ 
                          fontSize: '0.8rem', 
                          fontWeight: 500, 
                          color: '#1e293b',
                          lineHeight: 1.5
                        }}>
                          {index + 1}. {term.Name}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', gap: 1 }}>
          <Button 
            onClick={handleCloseTcModal} 
            variant="outlined"
            size="small"
            sx={{
              textTransform: 'none',
              fontSize: '0.8rem',
              borderColor: '#cbd5e1',
              color: '#475569',
              '&:hover': { borderColor: '#94a3b8', bgcolor: '#f1f5f9' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApplyTerms}
            variant="contained"
            size="small"
            disabled={selectedTerms.length === 0}
            sx={{ 
              textTransform: 'none',
              fontSize: '0.8rem',
              bgcolor: '#d97706', 
              '&:hover': { bgcolor: '#b45309' },
              '&:disabled': { bgcolor: '#cbd5e1', color: '#94a3b8' }
            }}
          >
            Apply Selected ({selectedTerms.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Add Supplier Modal */}
      <VendorModal
        open={vendorModalOpen}
        onClose={handleCloseVendorModal}
        modalType="vendor"
        modalAction="create"
        selectedItem={null}
        formData={vendorFormData}
        setFormData={setVendorFormData}
        onSave={handleSaveVendor}
        loading={vendorLoading}
      />

      {/* Quick Add Item Modal */}
      <ItemModal
        open={quickItemModalOpen}
        onClose={handleCloseQuickItemModal}
        onSave={handleQuickItemSaved}
        itemData={null}
        mode="create"
        categories={quickItemCategories}
        brands={quickItemBrands}
        uoms={quickItemUoms}
        existingItems={items}
      />
    </>
  );
};

export default PurchaseOrderModal;